import React, { useState, useEffect, useMemo, useRef } from 'react';
import ExerciseInteractiveMode from './ExerciseInteractiveMode';
import ExerciseCompleteOverlay from './ExerciseCompleteOverlay';

const FINISH_SECTION_ID = '__auto_finish_section__';
const EMPTY_COMPLETED = Object.freeze({});

/** Soft slate / teal Self-study palette (avoids purple Interactive look). */
const SS = {
    pageBg: 'radial-gradient(circle at top left, #f7f3eb 0%, #eef5f3 45%, #e8eef2 100%)',
    headerBg: 'rgba(255,255,255,0.92)',
    headerBorder: '1px solid rgba(214, 211, 209, 0.9)',
    headerShadow: '0 10px 24px rgba(28, 68, 64, 0.06)',
    accent: '#0f766e',
    accentSoft: '#ccfbf1',
    accentText: '#115e59',
    sectionBorder: '2px solid rgba(15, 118, 110, 0.16)',
    sectionBorderDone: '2px solid rgba(34, 197, 94, 0.35)',
    sectionHead: 'linear-gradient(90deg, #ecfdf5, #f8fafc)',
    sectionHeadDone: 'linear-gradient(90deg, #d1fae5, #ecfdf5)',
    cardBorder: '2px solid rgba(148, 163, 184, 0.28)',
    cardBorderDone: '2px solid rgba(34, 197, 94, 0.28)',
    cardHead: 'linear-gradient(90deg, #f8fafc, #f1f5f9)',
    cardHeadDone: 'linear-gradient(90deg, #dcfce7, #ecfdf5)',
    menuActive: 'linear-gradient(135deg, #0f766e, #0d9488)',
    progress: 'linear-gradient(90deg, #14b8a6, #0ea5e9)',
    nextBtn: 'linear-gradient(135deg, #0f766e, #0d9488)',
    finishBtn: 'linear-gradient(135deg, #059669, #10b981)',
    previewPill: { color: '#854d0e', background: '#fef9c3', border: '1px solid #fde047' },
};

function isAssignmentCompleted(assignment) {
    if (!assignment) return false;
    if (assignment.status === 'completed' || assignment.status === 'checked') return true;
    if (assignment.block_id || assignment.sub_statuses) {
        const subStatuses = assignment.sub_statuses || {};
        const ids = Object.keys(subStatuses);
        return ids.length > 0 && ids.every((subId) => {
            const status = subStatuses[subId]?.status;
            return status === 'completed' || status === 'checked';
        });
    }
    return false;
}

function isBlockAssignmentCompleted(assignment) {
    return isAssignmentCompleted(assignment);
}

function computeAwards(exercise, data = {}) {
    const assessment = exercise?.assessment || {};
    const score = data?.score || 0;
    const starsPerGap = parseFloat(assessment.starsPerGap) || 0;
    const coinsPerGap = parseInt(assessment.coinsPerGap, 10) || 0;
    const starsOnComplete = parseFloat(assessment.starsOnComplete) || 0;
    const coinsOnComplete = parseInt(assessment.coinsOnComplete, 10) || 0;
    return {
        stars: (score * starsPerGap) + starsOnComplete,
        coins: (score * coinsPerGap) + coinsOnComplete,
    };
}

/**
 * Shared Self-study lesson UI (student page + Full Lesson Self-study preview).
 */
export default function SelfStudyLessonView({
    previewMode = false,
    embedded = false,
    lesson,
    exercises = [],
    blocks = [],
    fullLessonSections = [],
    user = null,
    assignments: assignmentsProp = [],
    groupId = null,
    lessonProgress = null,
    initialCompletedSections = EMPTY_COMPLETED,
    onExit,
    onFinishLesson,
    isCompleting = false,
    onExerciseUpdate,
    onExerciseComplete,
    onBlockSubComplete,
}) {
    const [completedSections, setCompletedSections] = useState(() => ({ ...initialCompletedSections }));
    const [localAssignments, setLocalAssignments] = useState({});
    const [activeSectionId, setActiveSectionId] = useState(null);
    const [isSectionMenuOpen, setIsSectionMenuOpen] = useState(false);
    const [completeOverlay, setCompleteOverlay] = useState(null);
    const [expandedDoneIds, setExpandedDoneIds] = useState({});
    const scrollRootRef = useRef(null);
    const shouldScrollToTopRef = useRef(false);

    const scrollToSectionStart = () => {
        requestAnimationFrame(() => {
            const root = scrollRootRef.current;
            if (root && typeof root.scrollTo === 'function') {
                root.scrollTo({ top: 0, behavior: 'smooth' });
            } else if (typeof window !== 'undefined') {
                window.scrollTo({ top: 0, behavior: 'smooth' });
            }
        });
    };

    useEffect(() => {
        if (!shouldScrollToTopRef.current) return;
        shouldScrollToTopRef.current = false;
        scrollToSectionStart();
    }, [activeSectionId]);

    useEffect(() => {
        setCompletedSections({ ...(initialCompletedSections || EMPTY_COMPLETED) });
    }, [initialCompletedSections]);

    // Preview: seed local stub assignments so Complete works without DB rows
    useEffect(() => {
        if (!previewMode) return;
        const next = {};
        (fullLessonSections || []).forEach((section) => {
            (section.items || []).forEach((item) => {
                if (item.content_type === 'exercise') {
                    const key = `exercise:${item.content_id}`;
                    next[key] = {
                        id: `preview:${item.id}`,
                        status: 'assigned',
                        exercise_id: item.content_id,
                        block_id: null,
                        sub_statuses: {},
                    };
                } else if (item.content_type === 'block') {
                    const key = `block:${item.content_id}`;
                    next[key] = {
                        id: `preview:${item.id}`,
                        status: 'assigned',
                        exercise_id: null,
                        block_id: item.content_id,
                        sub_statuses: {},
                    };
                }
            });
        });
        setLocalAssignments((prev) => {
            // Keep any completed stubs from the current preview session
            const merged = { ...next };
            Object.entries(prev).forEach(([key, a]) => {
                if (a?.status === 'completed' || (a?.sub_statuses && Object.keys(a.sub_statuses).length)) {
                    merged[key] = a;
                }
            });
            return merged;
        });
    }, [previewMode, fullLessonSections]);

    const assignments = useMemo(() => {
        const base = Array.isArray(assignmentsProp) ? assignmentsProp : [];
        if (!previewMode) return base;
        const byKey = new Map();
        base.forEach((a) => {
            if (a?.exercise_id) byKey.set(`exercise:${a.exercise_id}`, a);
            if (a?.block_id) byKey.set(`block:${a.block_id}`, a);
        });
        Object.entries(localAssignments).forEach(([key, a]) => byKey.set(key, a));
        return Array.from(byKey.values());
    }, [assignmentsProp, localAssignments, previewMode]);

    const sections = useMemo(() => {
        const customSections = [...(fullLessonSections || [])]
            .sort((a, b) => (a.order_index || 0) - (b.order_index || 0))
            .map((section) => ({
                ...section,
                isAutoGenerated: false,
                items: Array.isArray(section.items)
                    ? [...section.items].sort((a, b) => (a.order_index || 0) - (b.order_index || 0))
                    : [],
            }));

        return [
            ...customSections,
            {
                id: FINISH_SECTION_ID,
                title: 'Finish Lesson',
                isAutoGenerated: true,
                items: [],
            },
        ];
    }, [fullLessonSections]);

    const getAssignmentForSectionItem = (item) => {
        if (!item) return null;
        const assignmentList = (Array.isArray(assignments) ? assignments : []).filter(
            (a) => !groupId || !a.group_id || String(a.group_id) === String(groupId)
        );
        if (item.content_type === 'exercise') {
            return assignmentList.find((a) => String(a.exercise_id || '') === String(item.content_id)) || null;
        }
        if (item.content_type === 'block') {
            return assignmentList.find((a) => String(a.block_id || '') === String(item.content_id)) || null;
        }
        return null;
    };

    const ensurePreviewAssignment = (item) => {
        if (!item) return null;
        return getAssignmentForSectionItem(item);
    };

    const isSectionItemDone = (item, assignment = null) => {
        if (lessonProgress?.is_completed) return true;
        if (item?.id && completedSections[item.id]) return true;
        const activeAssignment = assignment || getAssignmentForSectionItem(item);
        if (item?.content_type === 'block') return isBlockAssignmentCompleted(activeAssignment);
        return isAssignmentCompleted(activeAssignment);
    };

    const isSectionComplete = (section) => {
        if (!section) return false;
        if (section.isAutoGenerated) {
            if (previewMode) {
                // In preview, Finish is "complete" only after teacher clicks Finish (tracked locally)
                return !!completedSections[FINISH_SECTION_ID];
            }
            return !!lessonProgress?.is_completed;
        }
        if (!Array.isArray(section.items) || section.items.length === 0) return true;
        return section.items.every((item) => isSectionItemDone(item));
    };

    const isSectionUnlocked = (sectionIndex) => {
        if (previewMode) return true;
        if (sectionIndex <= 0) return true;
        return sections.slice(0, sectionIndex).every((section) => isSectionComplete(section));
    };

    useEffect(() => {
        if (!sections.length) return;
        const resumeSection = sections.find((section, index) => isSectionUnlocked(index) && !isSectionComplete(section))
            || sections.find((section, index) => isSectionUnlocked(index))
            || sections[0];

        setActiveSectionId((prev) => {
            if (prev) {
                const prevIndex = sections.findIndex((section) => section.id === prev);
                if (prevIndex >= 0 && isSectionUnlocked(prevIndex)) return prev;
            }
            return resumeSection?.id || sections[0].id;
        });
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [sections, completedSections, lessonProgress?.is_completed, previewMode]);

    const activeSectionData = sections.find((section) => section.id === activeSectionId) || sections[0] || null;
    const activeSectionIndex = activeSectionData ? sections.findIndex((section) => section.id === activeSectionData.id) : -1;
    const activeSectionNumber = activeSectionIndex >= 0 ? activeSectionIndex + 1 : 0;
    const lessonSections = sections.filter((section) => !section.isAutoGenerated);
    const completedCount = lessonSections.filter((section) => isSectionComplete(section)).length;
    const progressPercent = (!previewMode && lessonProgress?.is_completed)
        ? 100
        : (lessonSections.length > 0 ? Math.round((completedCount / lessonSections.length) * 100) : 0);
    const isCompleted = previewMode
        ? !!completedSections[FINISH_SECTION_ID]
        : !!lessonProgress?.is_completed;
    const activeSectionItems = activeSectionData?.items || [];
    const activeSectionDone = activeSectionData ? isSectionComplete(activeSectionData) : false;

    const markSectionItemComplete = (itemId) => {
        setCompletedSections((prev) => (prev[itemId] ? prev : { ...prev, [itemId]: true }));
    };

    const goToSection = (sectionId) => {
        const sectionIndex = sections.findIndex((section) => section.id === sectionId);
        if (sectionIndex < 0 || !isSectionUnlocked(sectionIndex)) return;
        shouldScrollToTopRef.current = true;
        setActiveSectionId(sectionId);
        setIsSectionMenuOpen(false);
    };

    const goToNextSection = () => {
        if (!activeSectionDone) return;
        if (activeSectionIndex < 0) return;
        const nextSection = sections[activeSectionIndex + 1];
        if (nextSection) {
            shouldScrollToTopRef.current = true;
            setActiveSectionId(nextSection.id);
            setIsSectionMenuOpen(false);
        }
    };

    const handleExerciseUpdate = async (assignment, data) => {
        if (previewMode) return;
        if (onExerciseUpdate) await onExerciseUpdate(assignment, data);
    };

    const handleExerciseComplete = async (item, assignment, data = {}) => {
        const exerciseMeta = (exercises || []).find((e) => String(e.id) === String(item.content_id));
        const isIllustrationAuto = !!(data?.autoViewed || exerciseMeta?.type === 'illustration');
        const awards = isIllustrationAuto ? { stars: 0, coins: 0 } : computeAwards(exerciseMeta, data);
        const now = new Date().toISOString();
        const nextStatus = isIllustrationAuto ? 'checked' : 'completed';

        if (previewMode) {
            const key = `exercise:${item.content_id}`;
            const stub = assignment || ensurePreviewAssignment(item) || {
                id: `preview:${item.id}`,
                exercise_id: item.content_id,
                status: 'assigned',
            };
            setLocalAssignments((prev) => ({
                ...prev,
                [key]: {
                    ...stub,
                    ...data,
                    status: nextStatus,
                    completed_at: now,
                    ...(isIllustrationAuto ? { checked_at: now, awarded_stars: 0, awarded_coins: 0 } : {}),
                },
            }));
            markSectionItemComplete(item.id);
            if (!isIllustrationAuto) {
                setCompleteOverlay({
                    type: 'success',
                    title: 'Exercise',
                    stars: awards.stars,
                    coins: awards.coins,
                    message: 'Preview: exercise completed. Nothing is saved.',
                });
            }
            return;
        }

        try {
            if (onExerciseComplete) await onExerciseComplete(item, assignment, {
                ...data,
                ...(isIllustrationAuto ? {
                    status: 'checked',
                    autoViewed: true,
                    awarded_stars: 0,
                    awarded_coins: 0,
                } : {}),
            });
            markSectionItemComplete(item.id);
            if (!isIllustrationAuto) {
                setCompleteOverlay({
                    type: 'success',
                    title: 'Exercise',
                    stars: awards.stars,
                    coins: awards.coins,
                    message: 'You\'ve completed this exercise. It was sent to the teacher for check. When it is checked, you will earn these rewards.',
                });
            }
        } catch (e) {
            setCompleteOverlay({
                type: 'error',
                title: 'Exercise',
                message: `We couldn't save your answers: ${e?.message || 'Unknown error'}. You can return and try again.`,
            });
        }
    };

    const handleBlockSubComplete = async (item, block, assignment, exercise, data = {}) => {
        const isIllustrationAuto = !!(data?.autoViewed || exercise?.type === 'illustration');
        const awards = isIllustrationAuto ? { stars: 0, coins: 0 } : computeAwards(exercise, data);
        const now = new Date().toISOString();
        const nextStatus = isIllustrationAuto ? 'checked' : 'completed';

        if (previewMode) {
            const key = `block:${item.content_id}`;
            const stub = assignment || ensurePreviewAssignment(item, block) || {
                id: `preview:${item.id}`,
                block_id: item.content_id,
                status: 'assigned',
                sub_statuses: {},
            };
            const subStatuses = { ...(stub.sub_statuses || {}) };
            subStatuses[exercise.id] = {
                ...data,
                status: nextStatus,
                completed_at: now,
                ...(isIllustrationAuto ? { checked_at: now } : {}),
            };
            const blockExerciseIds = Array.isArray(block?.exercise_ids) ? block.exercise_ids.map((id) => String(id)) : [];
            const allDone = blockExerciseIds.length > 0 && blockExerciseIds.every((exId) => {
                const status = subStatuses[exId]?.status;
                return status === 'completed' || status === 'checked';
            });
            setLocalAssignments((prev) => ({
                ...prev,
                [key]: {
                    ...stub,
                    sub_statuses: subStatuses,
                    ...(allDone ? { status: 'completed', completed_at: now } : {}),
                },
            }));
            if (allDone) markSectionItemComplete(item.id);
            if (!isIllustrationAuto) {
                setCompleteOverlay({
                    type: 'success',
                    title: 'Exercise',
                    stars: awards.stars,
                    coins: awards.coins,
                    message: 'Preview: exercise completed in the block. Nothing is saved.',
                });
            }
            return;
        }

        try {
            if (onBlockSubComplete) await onBlockSubComplete(item, block, assignment, exercise, {
                ...data,
                status: nextStatus,
                ...(isIllustrationAuto ? { autoViewed: true, awarded_stars: 0, awarded_coins: 0 } : {}),
            });
            const subStatuses = { ...(assignment?.sub_statuses || {}) };
            subStatuses[exercise.id] = { status: nextStatus };
            const blockExerciseIds = Array.isArray(block?.exercise_ids) ? block.exercise_ids.map((id) => String(id)) : [];
            const allDone = blockExerciseIds.length > 0 && blockExerciseIds.every((exId) => {
                const status = subStatuses[exId]?.status || (exId === String(exercise.id) ? nextStatus : null);
                return status === 'completed' || status === 'checked';
            });
            if (allDone) markSectionItemComplete(item.id);
            if (!isIllustrationAuto) {
                setCompleteOverlay({
                    type: 'success',
                    title: 'Exercise',
                    stars: awards.stars,
                    coins: awards.coins,
                    message: 'You\'ve completed this exercise in the block. It was sent to the teacher for check.',
                });
            }
        } catch (e) {
            setCompleteOverlay({
                type: 'error',
                title: 'Exercise',
                message: `We couldn't save your answers: ${e?.message || 'Unknown error'}. You can return and try again.`,
            });
        }
    };

    const handleFinish = async () => {
        if (previewMode) {
            markSectionItemComplete(FINISH_SECTION_ID);
            if (onFinishLesson) onFinishLesson();
            return;
        }
        if (onFinishLesson) await onFinishLesson();
    };

    if (!lesson) {
        return (
            <div style={{ padding: 40, textAlign: 'center', color: '#64748b' }}>
                Lesson not found
            </div>
        );
    }

    /** Student-facing label: short instruction, else title (bank titles are secondary). */
    const studentExerciseLabel = (exercise) => {
        const instruction = String(exercise?.instruction || '').trim().split(/\n/)[0]?.trim();
        if (instruction) return instruction;
        const title = String(exercise?.title || '').trim();
        if (title) return title;
        return 'Exercise';
    };

    const renderStudentExerciseCard = ({
        key,
        exercise,
        assignment,
        isDone,
        displayNumber,
        onUpdate,
        onComplete,
        completedPlaceholder = false,
    }) => {
        const canCollapse = completedPlaceholder && isDone && exercise?.type !== 'illustration';
        const isExpanded = !canCollapse || !!expandedDoneIds[key];
        const toggleExpanded = () => {
            if (!canCollapse) return;
            setExpandedDoneIds((prev) => ({ ...prev, [key]: !prev[key] }));
        };
        return (
        <div key={key} style={{ borderRadius: 22, overflow: 'hidden', border: isDone ? SS.cardBorderDone : SS.cardBorder, background: isDone ? 'linear-gradient(180deg, rgba(236,253,245,0.96), rgba(255,255,255,0.96))' : 'white', boxShadow: '0 8px 20px rgba(28, 68, 64, 0.05)' }}>
            <div
                role={canCollapse ? 'button' : undefined}
                tabIndex={canCollapse ? 0 : undefined}
                aria-expanded={canCollapse ? isExpanded : undefined}
                onClick={canCollapse ? toggleExpanded : undefined}
                onKeyDown={canCollapse ? (e) => {
                    if (e.key === 'Enter' || e.key === ' ') {
                        e.preventDefault();
                        toggleExpanded();
                    }
                } : undefined}
                style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12, padding: '14px 16px', borderBottom: isExpanded ? '1px solid rgba(148, 163, 184, 0.16)' : 'none', background: isDone ? SS.cardHeadDone : SS.cardHead, cursor: canCollapse ? 'pointer' : 'default' }}
            >
                <div style={{ minWidth: 0 }}>
                    <div style={{ fontWeight: 900, color: '#1e293b', fontSize: 16 }}>
                        {displayNumber}. {studentExerciseLabel(exercise)}
                    </div>
                    {canCollapse && (
                        <div style={{ marginTop: 2, fontSize: 12, fontWeight: 800, color: '#047857' }}>
                            {isExpanded ? 'Hide answers ▴' : 'View answers ▾'}
                        </div>
                    )}
                </div>
                <span style={{ padding: '6px 10px', borderRadius: 999, fontSize: 12, fontWeight: 900, color: isDone ? '#065f46' : SS.accentText, background: isDone ? '#d1fae5' : SS.accentSoft, flexShrink: 0 }}>{isDone ? '✓ Done' : 'Not started'}</span>
            </div>
            {isExpanded && (
            <div style={{ padding: 18 }}>
                {!exercise ? (
                    <div style={{ padding: 20, textAlign: 'center', color: '#94a3b8', fontWeight: 700 }}>This exercise could not be loaded.</div>
                ) : (
                    <ExerciseInteractiveMode
                        exercise={exercise}
                        assignment={assignment}
                        onUpdate={onUpdate}
                        onComplete={onComplete}
                        preview={false}
                        isOnlineLesson={false}
                        isTeacher={false}
                        practiceMode="individual"
                        user={user}
                    />
                )}
            </div>
            )}
        </div>
        );
    };

    /** Flatten section items into a student exercise list (blocks expand; no Block/Exercise wrappers). */
    const flattenSectionStudentExercises = (items = []) => {
        const rows = [];
        (items || []).forEach((item) => {
            if (item.content_type === 'exercise') {
                rows.push({ kind: 'exercise', item, exerciseId: item.content_id });
                return;
            }
            if (item.content_type === 'block') {
                const block = blocks.find((b) => String(b.id) === String(item.content_id));
                const ids = Array.isArray(block?.exercise_ids)
                    ? block.exercise_ids
                    : Array.isArray(block?.exercises)
                        ? block.exercises.map((e) => e?.id || e).filter(Boolean)
                        : [];
                if (!ids.length) {
                    rows.push({ kind: 'empty_block', item, block });
                    return;
                }
                ids.forEach((exerciseId) => {
                    rows.push({ kind: 'block_exercise', item, block, exerciseId });
                });
                return;
            }
            if (item.content_type === 'exercise_idea') {
                rows.push({ kind: 'idea', item });
                return;
            }
            rows.push({ kind: 'unsupported', item });
        });
        return rows;
    };

    const outerStyle = embedded
        ? {
            height: '100%',
            overflow: 'auto',
            padding: '18px',
            background: SS.pageBg,
            boxSizing: 'border-box',
        }
        : {
            minHeight: '100vh',
            padding: '18px',
            background: SS.pageBg,
        };

    let exerciseCounter = 0;

    return (
        <div ref={scrollRootRef} className="self-study-lesson" style={outerStyle}>
            <style>{`
                .self-study-lesson .exercise-instruction-banner {
                    background: #f1f5f9 !important;
                    color: #64748b !important;
                    font-weight: 600 !important;
                    border-bottom: 1px solid #e2e8f0 !important;
                }
                .self-study-lesson .exercise-instruction-banner--info {
                    background: linear-gradient(90deg, #f59e0b 0%, #ea580c 100%) !important;
                    color: #fff !important;
                    border-bottom: none !important;
                }
                .self-study-lesson .exercise-notes-under-instruction {
                    background: linear-gradient(90deg, #e0e7ff 0%, #ede9fe 100%) !important;
                    color: #312e81 !important;
                    border-bottom: 1px solid #c7d2fe !important;
                }
                .self-study-lesson .exercise-notes-under-instruction--info {
                    background: linear-gradient(90deg, #ffedd5 0%, #fed7aa 100%) !important;
                    color: #9a3412 !important;
                    border-bottom: 1px solid #fdba74 !important;
                }
                .self-study-lesson .exercise-card-shell {
                    background: #ffffff !important;
                    border-color: #e2e8f0 !important;
                }
            `}</style>
            <div style={{ maxWidth: 1180, margin: '0 auto', display: 'flex', flexDirection: 'column', gap: 18 }}>
                <div style={{
                    position: 'sticky',
                    top: embedded ? 0 : 10,
                    zIndex: 20,
                    backdropFilter: 'blur(12px)',
                    background: SS.headerBg,
                    border: SS.headerBorder,
                    borderRadius: 18,
                    boxShadow: SS.headerShadow,
                    padding: '8px 12px',
                }}>
                    <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 14, flexWrap: 'nowrap' }}>
                        <div style={{ minWidth: 0, flex: '1 1 auto' }}>
                            <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
                                <h1 style={{ margin: 0, fontSize: 22, fontWeight: 900, color: '#1e293b' }}>
                                    {lesson?.course || 'Self-study course'}
                                </h1>
                                {previewMode && (
                                    <span style={{ padding: '4px 10px', borderRadius: 999, fontSize: 11, fontWeight: 900, ...SS.previewPill }}>
                                        Self-study preview
                                    </span>
                                )}
                            </div>
                            <div style={{ marginTop: 2, color: '#78716c', fontWeight: 700, fontSize: 12 }}>
                                Lesson {lesson?.lesson} {lesson?.title ? `• ${lesson.title}` : ''}
                            </div>
                        </div>

                        <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end', gap: 8, position: 'relative', marginTop: 0, flex: '0 0 auto' }}>
                            {typeof onExit === 'function' && (
                                <button
                                    type="button"
                                    onClick={onExit}
                                    style={{
                                        border: 'none',
                                        cursor: 'pointer',
                                        borderRadius: 999,
                                        padding: '7px 14px',
                                        fontWeight: 800,
                                        fontSize: 13,
                                        color: 'white',
                                        background: 'linear-gradient(135deg, #e11d48, #f97316)',
                                        boxShadow: '0 8px 18px rgba(225, 29, 72, 0.16)',
                                    }}
                                >
                                    Exit
                                </button>
                            )}

                            <button
                                type="button"
                                onClick={() => setIsSectionMenuOpen((prev) => !prev)}
                                style={{
                                    border: '1px solid #e7e5e4',
                                    cursor: 'pointer',
                                    borderRadius: 999,
                                    padding: '7px 14px',
                                    fontWeight: 900,
                                    fontSize: 12,
                                    color: '#44403c',
                                    background: '#fafaf9',
                                    boxShadow: '0 4px 10px rgba(28, 25, 23, 0.04)',
                                }}
                            >
                                ☰ Menu
                            </button>

                            {isSectionMenuOpen && (
                                <>
                                    <div
                                        onClick={() => setIsSectionMenuOpen(false)}
                                        style={{ position: 'fixed', inset: 0, background: 'rgba(15, 23, 42, 0.18)', zIndex: 30 }}
                                    />
                                    <div style={{ position: 'absolute', right: 0, top: 'calc(100% + 10px)', width: 300, zIndex: 40, background: 'white', borderRadius: 18, border: '1px solid rgba(226,232,240,0.95)', boxShadow: '0 18px 40px rgba(15, 23, 42, 0.16)', overflow: 'hidden' }}>
                                        <div style={{ padding: '14px 16px', borderBottom: '1px solid #e7e5e4', background: 'linear-gradient(135deg, #fafaf9, #f0fdfa)' }}>
                                            <div style={{ fontSize: 12, fontWeight: 900, color: '#78716c', textTransform: 'uppercase', letterSpacing: '0.08em' }}>Content plan</div>
                                            <div style={{ marginTop: 4, fontSize: 13, fontWeight: 700, color: '#57534e' }}>
                                                {previewMode
                                                    ? 'Preview: all sections are unlocked for browsing.'
                                                    : 'Locked sections are grey until the previous section is complete.'}
                                            </div>
                                        </div>

                                        <div style={{ maxHeight: 340, overflowY: 'auto', padding: 10 }}>
                                            {sections.map((section, sectionIndex) => {
                                                const sectionUnlocked = isSectionUnlocked(sectionIndex);
                                                const sectionComplete = isSectionComplete(section);
                                                const isActive = section.id === activeSectionId;
                                                const locked = !sectionUnlocked;

                                                return (
                                                    <button
                                                        key={section.id}
                                                        type="button"
                                                        disabled={locked}
                                                        onClick={() => goToSection(section.id)}
                                                        style={{
                                                            width: '100%',
                                                            border: 'none',
                                                            cursor: locked ? 'not-allowed' : 'pointer',
                                                            padding: '10px 12px',
                                                            borderRadius: 14,
                                                            background: locked
                                                                ? '#f5f5f4'
                                                                : isActive
                                                                    ? SS.menuActive
                                                                    : sectionComplete
                                                                        ? '#ecfdf5'
                                                                        : '#fafaf9',
                                                            color: locked
                                                                ? '#a8a29e'
                                                                : isActive
                                                                    ? 'white'
                                                                    : '#44403c',
                                                            display: 'flex',
                                                            alignItems: 'center',
                                                            justifyContent: 'space-between',
                                                            gap: 10,
                                                            marginBottom: 8,
                                                            textAlign: 'left',
                                                            opacity: locked ? 0.75 : 1,
                                                        }}
                                                    >
                                                        <span style={{ display: 'flex', alignItems: 'center', gap: 10, minWidth: 0 }}>
                                                            <span style={{ width: 24, height: 24, borderRadius: '50%', display: 'inline-flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0, fontSize: 11, fontWeight: 900, background: locked ? '#cbd5e1' : isActive ? 'rgba(255,255,255,0.18)' : (sectionComplete ? '#d1fae5' : '#e2e8f0'), color: locked ? '#64748b' : isActive ? 'white' : (sectionComplete ? '#065f46' : '#475569') }}>
                                                                {sectionIndex + 1}
                                                            </span>
                                                            <span style={{ minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', fontWeight: 800, fontSize: 13 }}>
                                                                {section.title}
                                                            </span>
                                                        </span>

                                                        <span style={{ flexShrink: 0, fontSize: 11, fontWeight: 900, color: locked ? '#94a3b8' : sectionComplete ? '#065f46' : '#475569' }}>
                                                            {locked ? 'Locked' : sectionComplete ? (isActive ? 'Current' : 'Done') : 'Open'}
                                                        </span>
                                                    </button>
                                                );
                                            })}
                                        </div>
                                    </div>
                                </>
                            )}
                        </div>
                    </div>

                    <div style={{ marginTop: 8 }}>
                        <div style={{ width: '100%', height: 10, borderRadius: 999, background: '#e7e5e4', overflow: 'hidden' }}>
                            <div style={{ width: `${progressPercent}%`, height: '100%', borderRadius: 999, background: SS.progress, transition: 'width 0.35s ease' }} />
                        </div>
                    </div>
                </div>

                <div style={{ display: 'flex', flexDirection: 'column', gap: 18 }}>
                    {activeSectionData ? (
                        <>
                            <div
                                style={{
                                    borderRadius: 26,
                                    overflow: 'hidden',
                                    border: activeSectionDone ? SS.sectionBorderDone : SS.sectionBorder,
                                    background: activeSectionDone
                                        ? 'linear-gradient(180deg, rgba(236,253,245,0.98), rgba(255,255,255,0.98))'
                                        : 'linear-gradient(180deg, rgba(255,255,255,0.98), rgba(250,250,249,0.98))',
                                    boxShadow: '0 14px 30px rgba(28, 25, 23, 0.06)',
                                }}
                            >
                                <div style={{
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'space-between',
                                    gap: 12,
                                    padding: '16px 18px',
                                    background: activeSectionDone ? SS.sectionHeadDone : SS.sectionHead,
                                }}>
                                    <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                                        <div style={{ width: 40, height: 40, borderRadius: 14, display: 'flex', alignItems: 'center', justifyContent: 'center', fontWeight: 900, color: 'white', background: activeSectionDone ? 'linear-gradient(135deg, #10b981, #14b8a6)' : SS.menuActive }}>{activeSectionNumber || 1}</div>
                                        <div>
                                            <div style={{ fontSize: 12, fontWeight: 900, color: '#78716c', textTransform: 'uppercase', letterSpacing: '0.08em' }}>Section {activeSectionNumber || 1}</div>
                                            <div style={{ fontSize: 18, fontWeight: 900, color: '#1c1917', marginTop: 2 }}>{activeSectionData.title}</div>
                                        </div>
                                    </div>
                                    <span style={{ padding: '7px 12px', borderRadius: 999, fontSize: 12, fontWeight: 900, color: activeSectionDone ? '#065f46' : SS.accentText, background: activeSectionDone ? '#d1fae5' : SS.accentSoft }}>{activeSectionDone ? '✓ Done' : 'In progress'}</span>
                                </div>
                            </div>

                            {activeSectionData.isAutoGenerated ? (
                                <div
                                    style={{
                                        borderRadius: 26,
                                        overflow: 'hidden',
                                        border: '2px solid rgba(34, 197, 94, 0.35)',
                                        background: 'linear-gradient(180deg, rgba(236,253,245,0.98), rgba(255,255,255,0.98))',
                                        boxShadow: '0 14px 30px rgba(15, 23, 42, 0.08)',
                                    }}
                                >
                                    <div style={{ padding: 18 }}>
                                        <div style={{ padding: 24, borderRadius: 18, border: '1px solid #e2e8f0', background: 'white', textAlign: 'center' }}>
                                            <div style={{ fontSize: 18, fontWeight: 900, color: '#0f172a', marginBottom: 8 }}>Congratulations! You've reached the final section.</div>
                                            <div style={{ color: '#64748b', fontWeight: 600, marginBottom: 18 }}>
                                                {previewMode ? 'Preview only — Finish does not unlock the next lesson.' : 'Click to complete the lesson.'}
                                            </div>
                                            {isCompleted ? (
                                                <div style={{ color: '#10b981', fontWeight: 800, fontSize: 14 }}>✓ This lesson has been completed</div>
                                            ) : (
                                                <button
                                                    type="button"
                                                    onClick={handleFinish}
                                                    disabled={isCompleting}
                                                    style={{
                                                        padding: '12px 24px',
                                                        background: isCompleting ? '#94a3b8' : SS.finishBtn,
                                                        color: 'white',
                                                        border: 'none',
                                                        borderRadius: 999,
                                                        cursor: isCompleting ? 'not-allowed' : 'pointer',
                                                        fontWeight: 900,
                                                        fontSize: 14,
                                                        boxShadow: '0 12px 28px rgba(16, 185, 129, 0.22)',
                                                    }}
                                                >
                                                    {isCompleting ? 'Completing...' : (previewMode ? 'Finish Lesson (preview)' : 'Finish Lesson')}
                                                </button>
                                            )}
                                            <p style={{ margin: '10px 0 0', color: '#64748b', fontSize: 12, fontWeight: 600 }}>
                                                {isCompleted
                                                    ? (previewMode ? 'Preview finish marked locally.' : 'You can now move to the next lesson.')
                                                    : (previewMode ? 'No progress is saved in preview.' : 'Finish this final section to complete the lesson.')}
                                            </p>
                                        </div>
                                    </div>
                                </div>
                            ) : activeSectionItems.length > 0 ? (
                                <div
                                    style={{
                                        borderRadius: 26,
                                        overflow: 'hidden',
                                        border: activeSectionDone ? SS.sectionBorderDone : SS.sectionBorder,
                                        background: activeSectionDone
                                            ? 'linear-gradient(180deg, rgba(236,253,245,0.98), rgba(255,255,255,0.98))'
                                            : 'linear-gradient(180deg, rgba(255,255,255,0.98), rgba(250,250,249,0.98))',
                                        boxShadow: '0 14px 30px rgba(28, 25, 23, 0.06)',
                                    }}
                                >
                                    <div style={{ padding: 18 }}>
                                        <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
                                            {flattenSectionStudentExercises(activeSectionItems).map((row) => {
                                                if (row.kind === 'exercise') {
                                                    exerciseCounter += 1;
                                                    const { item } = row;
                                                    const assignment = getAssignmentForSectionItem(item);
                                                    const isDone = completedSections[item.id] || isSectionItemDone(item, assignment);
                                                    const exercise = exercises.find((ex) => String(ex.id) === String(row.exerciseId));
                                                    const activeAssignment = assignment || (previewMode ? ensurePreviewAssignment(item) : null);
                                                    return renderStudentExerciseCard({
                                                        key: item.id,
                                                        exercise,
                                                        assignment: activeAssignment,
                                                        isDone,
                                                        displayNumber: exerciseCounter,
                                                        onUpdate: (data) => handleExerciseUpdate(activeAssignment, data),
                                                        onComplete: (data) => handleExerciseComplete(item, activeAssignment, data),
                                                    });
                                                }

                                                if (row.kind === 'block_exercise') {
                                                    exerciseCounter += 1;
                                                    const { item, block, exerciseId } = row;
                                                    const assignment = getAssignmentForSectionItem(item);
                                                    const activeAssignment = assignment || (previewMode ? ensurePreviewAssignment(item) : null);
                                                    const exercise = exercises.find((ex) => String(ex.id) === String(exerciseId));
                                                    const subRecord = activeAssignment?.sub_statuses?.[exerciseId]
                                                        || activeAssignment?.sub_statuses?.[String(exerciseId)]
                                                        || {};
                                                    const subStatus = subRecord.status;
                                                    const subDone = subStatus === 'completed' || subStatus === 'checked'
                                                        || !!(item?.id && completedSections[item.id])
                                                        || !!lessonProgress?.is_completed;
                                                    const subAssignment = {
                                                        id: `${activeAssignment?.id || item.id}:${exerciseId}`,
                                                        status: subStatus || 'assigned',
                                                        answers: subRecord.answers,
                                                        locked: subRecord.locked,
                                                        is_correct: subRecord.is_correct,
                                                        completed_at: subRecord.completed_at,
                                                        checked_at: subRecord.checked_at,
                                                        gap_rewards: subRecord.gap_rewards,
                                                        score: subRecord.score,
                                                        total: subRecord.total,
                                                        teacher_comment: subRecord.teacher_comment || '',
                                                        block_status: activeAssignment?.status || null,
                                                    };
                                                    return renderStudentExerciseCard({
                                                        key: `${item.id}:${exerciseId}`,
                                                        exercise,
                                                        assignment: subAssignment,
                                                        isDone: subDone,
                                                        displayNumber: exerciseCounter,
                                                        completedPlaceholder: true,
                                                        onUpdate: (data) => handleExerciseUpdate(subAssignment, data),
                                                        onComplete: (data) => handleBlockSubComplete(item, block, activeAssignment, exercise, data),
                                                    });
                                                }

                                                if (row.kind === 'empty_block') {
                                                    return (
                                                        <div key={row.item.id} style={{ borderRadius: 22, padding: 20, background: 'white', border: '1.5px solid #e7e5e4', color: '#94a3b8', fontWeight: 700, textAlign: 'center' }}>
                                                            No exercises in this section item yet.
                                                        </div>
                                                    );
                                                }

                                                if (row.kind === 'idea') {
                                                    if (!previewMode) return null;
                                                    const note = row.item.payload?.note || '';
                                                    return (
                                                        <div key={row.item.id} style={{ borderRadius: 22, padding: 20, background: '#fffbeb', border: '1.5px dashed #f59e0b', color: '#92400e', fontWeight: 700 }}>
                                                            <div style={{ fontSize: 12, fontWeight: 900, textTransform: 'uppercase', letterSpacing: '0.06em', marginBottom: 6 }}>Exercise idea</div>
                                                            {note.trim() || 'Empty idea note (not shown to students as an exercise).'}
                                                        </div>
                                                    );
                                                }

                                                return (
                                                    <div key={row.item.id} style={{ borderRadius: 22, padding: 20, background: 'white', border: '1.5px solid #e7e5e4', color: '#78716c', fontWeight: 700 }}>
                                                        Unsupported content item in this section.
                                                    </div>
                                                );
                                            })}

                                            <div style={{ marginTop: 4, padding: 20, borderRadius: 22, background: activeSectionDone ? 'linear-gradient(135deg, #ecfdf5, #fafaf9)' : 'linear-gradient(135deg, #f0fdfa, #fafaf9)', border: '1px solid rgba(168,162,158,0.2)', textAlign: 'center' }}>
                                                <div style={{ fontSize: 18, fontWeight: 900, color: '#1c1917' }}>
                                                    {activeSectionDone
                                                        ? `Congratulations! You've completed Section ${activeSectionNumber}.`
                                                        : `Complete Section ${activeSectionNumber} to continue.`}
                                                </div>
                                                <div style={{ marginTop: 6, color: '#78716c', fontWeight: 600 }}>
                                                    {activeSectionDone ? 'Click to continue to the next section.' : 'Finish all exercises in this section first.'}
                                                </div>
                                                <button
                                                    type="button"
                                                    onClick={goToNextSection}
                                                    disabled={!activeSectionDone}
                                                    style={{
                                                        marginTop: 14,
                                                        padding: '12px 20px',
                                                        borderRadius: 999,
                                                        border: 'none',
                                                        cursor: activeSectionDone ? 'pointer' : 'not-allowed',
                                                        background: activeSectionDone ? SS.nextBtn : '#d6d3d1',
                                                        color: 'white',
                                                        fontWeight: 900,
                                                        boxShadow: activeSectionDone ? '0 12px 28px rgba(15, 118, 110, 0.22)' : 'none',
                                                    }}
                                                >
                                                    Go Next section
                                                </button>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            ) : (
                                <div
                                    style={{
                                        borderRadius: 26,
                                        overflow: 'hidden',
                                        border: '2px solid rgba(99, 102, 241, 0.18)',
                                        background: 'linear-gradient(180deg, rgba(255,255,255,0.98), rgba(248,250,252,0.98))',
                                        boxShadow: '0 14px 30px rgba(15, 23, 42, 0.08)',
                                    }}
                                >
                                    <div style={{ padding: 24, textAlign: 'center', color: '#94a3b8', fontWeight: 700 }}>
                                        This section has no content yet.
                                    </div>
                                </div>
                            )}
                        </>
                    ) : null}
                </div>
            </div>

            {completeOverlay && (
                <ExerciseCompleteOverlay
                    type={completeOverlay.type}
                    exerciseTitle={completeOverlay.title}
                    stars={completeOverlay.stars || 0}
                    coins={completeOverlay.coins || 0}
                    message={completeOverlay.message}
                    onSecondary={() => setCompleteOverlay(null)}
                    onPrimary={() => setCompleteOverlay(null)}
                    primaryLabel="OK"
                    secondaryLabel="Return"
                />
            )}
        </div>
    );
}
